import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import { CircularProgress } from "@mui/material";
import axiosInstance from "../../config/axiosConfig";

const IT_OFFICE_ID = "6a51b6dd21d7db8e31da818d";

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const emptyOffering = {
  title: "",
  description: "",
  order: 0,
  isActive: true,
};

const ItServicesAdmin = () => {
  const user = useSelector((state) => state.auth.user);
  const userDisplayName = user?.name || user?.email || "Admin";

  const [tab, setTab] = useState("services");

  // Office page fields
  const [office, setOffice] = useState(null);
  const [officeForm, setOfficeForm] = useState({
    description: "",
    contactName: "",
    contactEmail: "",
  });
  const [officeSaving, setOfficeSaving] = useState(false);

  // Offerings
  const [offerings, setOfferings] = useState([]);
  const [offeringsLoading, setOfferingsLoading] = useState(false);
  const [offeringForm, setOfferingForm] = useState(emptyOffering);
  const [editingOfferingId, setEditingOfferingId] = useState(null);
  const [offeringSaving, setOfferingSaving] = useState(false);

  // Profile requests
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [filter, setFilter] = useState("pending");
  const [selected, setSelected] = useState(null);
  const [adminNote, setAdminNote] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const fetchOffice = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`office/single/${IT_OFFICE_ID}`);
      const data = res.data?.data || null;
      setOffice(data);
      if (data) {
        setOfficeForm({
          description: data.description || "",
          contactName: data.contactName || "",
          contactEmail: data.contactEmail || "",
        });
      }
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Failed to load IT Services office", "error");
    }
  }, []);

  const fetchOfferings = useCallback(async () => {
    setOfferingsLoading(true);
    try {
      const res = await axiosInstance.get("it-services/offerings/all");
      setOfferings(res.data?.data || []);
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Failed to load service offerings", "error");
    }
    setOfferingsLoading(false);
  }, []);

  const fetchRequests = useCallback(async () => {
    setRequestsLoading(true);
    try {
      const res = await axiosInstance.get("it-services/profile-requests/all", {
        params: { status: filter },
      });
      setRequests(res.data?.data || []);
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Failed to load profile update requests", "error");
    }
    setRequestsLoading(false);
  }, [filter]);

  useEffect(() => {
    fetchOffice();
    fetchOfferings();
  }, [fetchOffice, fetchOfferings]);

  useEffect(() => {
    if (tab === "requests") fetchRequests();
  }, [tab, fetchRequests]);

  useEffect(() => {
    if (selected) setAdminNote(selected.adminNote || "");
  }, [selected]);

  const saveOffice = async (e) => {
    e.preventDefault();
    setOfficeSaving(true);
    try {
      const body = new FormData();
      body.append("description", officeForm.description);
      body.append("contactName", officeForm.contactName);
      body.append("contactEmail", officeForm.contactEmail);
      const res = await axiosInstance.post(
        `office/change/${IT_OFFICE_ID}`,
        body
      );
      setOffice(res.data?.data || office);
      Swal.fire("Saved", "IT Services page details updated", "success");
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to update office",
        "error"
      );
    }
    setOfficeSaving(false);
  };

  const resetOfferingForm = () => {
    setOfferingForm(emptyOffering);
    setEditingOfferingId(null);
  };

  const startEditOffering = (item) => {
    setEditingOfferingId(item._id);
    setOfferingForm({
      title: item.title || "",
      description: item.description || "",
      order: item.order ?? 0,
      isActive: item.isActive !== false,
    });
  };

  const saveOffering = async (e) => {
    e.preventDefault();
    if (!offeringForm.title.trim()) {
      Swal.fire("Missing title", "Title is required", "warning");
      return;
    }
    setOfferingSaving(true);
    try {
      const payload = {
        title: offeringForm.title,
        description: offeringForm.description,
        order: Number(offeringForm.order) || 0,
        isActive: offeringForm.isActive,
      };
      if (editingOfferingId) {
        await axiosInstance.put(
          `it-services/offerings/change/${editingOfferingId}`,
          payload
        );
        Swal.fire("Saved", "Service updated", "success");
      } else {
        await axiosInstance.post("it-services/offerings/create", payload);
        Swal.fire("Saved", "Service added", "success");
      }
      resetOfferingForm();
      fetchOfferings();
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to save service",
        "error"
      );
    }
    setOfferingSaving(false);
  };

  const deleteOffering = async (id) => {
    const result = await Swal.fire({
      title: "Delete this service?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Delete",
    });
    if (!result.isConfirmed) return;
    try {
      await axiosInstance.delete(`it-services/offerings/delete/${id}`);
      if (editingOfferingId === id) resetOfferingForm();
      fetchOfferings();
      Swal.fire("Deleted", "Service removed", "success");
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to delete",
        "error"
      );
    }
  };

  const openRequest = (msg) => {
    setSelected(msg);
    setAdminNote(msg.adminNote || "");
  };

  const closeDetail = () => {
    setSelected(null);
    setAdminNote("");
  };

  const markResponded = async () => {
    if (!selected) return;
    setActionLoading(true);
    try {
      const response = await axiosInstance.put(
        `it-services/profile-requests/change/${selected._id}`,
        { status: "responded", adminNote }
      );
      Swal.fire("Success", "Marked as responded", "success");
      setSelected(response.data?.data || null);
      fetchRequests();
      if (filter === "pending") closeDetail();
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to update",
        "error"
      );
    }
    setActionLoading(false);
  };

  const markPending = async () => {
    if (!selected) return;
    setActionLoading(true);
    try {
      const response = await axiosInstance.put(
        `it-services/profile-requests/change/${selected._id}`,
        { status: "pending", adminNote }
      );
      Swal.fire("Success", "Moved back to pending", "success");
      setSelected(response.data?.data || null);
      fetchRequests();
      if (filter === "responded") closeDetail();
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to update",
        "error"
      );
    }
    setActionLoading(false);
  };

  const deleteRequest = async (id) => {
    const result = await Swal.fire({
      title: "Delete this request?",
      text: "Uploaded files will be removed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Delete",
    });
    if (!result.isConfirmed) return;
    setActionLoading(true);
    try {
      await axiosInstance.delete(`it-services/profile-requests/delete/${id}`);
      Swal.fire("Deleted", "Request removed", "success");
      if (selected?._id === id) closeDetail();
      fetchRequests();
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to delete",
        "error"
      );
    }
    setActionLoading(false);
  };

  const tabBtn = (key, label) => (
    <button
      key={key}
      type="button"
      onClick={() => setTab(key)}
      style={{
        padding: "10px 18px",
        marginRight: 8,
        border: "none",
        borderRadius: 6,
        cursor: "pointer",
        background: tab === key ? "#4a90e2" : "#e0e0e0",
        color: tab === key ? "#fff" : "#333",
        fontWeight: 600,
      }}
    >
      {label}
    </button>
  );

  const filterBtn = (value, label) => (
    <button
      key={value}
      type="button"
      onClick={() => {
        setFilter(value);
        closeDetail();
      }}
      style={{
        padding: "8px 16px",
        marginRight: 8,
        backgroundColor: filter === value ? "#4CAF50" : "#ccc",
        color: "#fff",
        border: "none",
        borderRadius: 5,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ padding: 20 }}>
      <h1 style={{ marginTop: 0, marginBottom: 8 }}>IT Services</h1>
      <p style={{ marginTop: 0, color: "#666", fontSize: 14 }}>
        Manage the Directorate of IT Services public page (
        <code>/offices/directorate-of-it-services-…</code>) and review profile
        update requests.
      </p>

      <div style={{ marginBottom: 20 }}>
        {tabBtn("services", "Page & services")}
        {tabBtn("requests", "Profile update requests")}
      </div>

      {tab === "services" && (
        <>
          <section
            style={{
              border: "1px solid #e0e0e0",
              borderRadius: 8,
              padding: 16,
              marginBottom: 24,
              background: "#fff",
            }}
          >
            <h2 style={{ marginTop: 0 }}>Page details</h2>
            <p style={{ color: "#666", fontSize: 13, marginTop: 0 }}>
              Description and contact shown on the public IT Services page
              {office?.name ? ` (${office.name})` : ""}.
            </p>
            <form onSubmit={saveOffice}>
              <label style={{ display: "block", marginBottom: 12 }}>
                Description
                <textarea
                  value={officeForm.description}
                  onChange={(e) =>
                    setOfficeForm({ ...officeForm, description: e.target.value })
                  }
                  rows={4}
                  style={{
                    display: "block",
                    width: "100%",
                    marginTop: 4,
                    padding: 8,
                    boxSizing: "border-box",
                  }}
                />
              </label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 12,
                  marginBottom: 12,
                }}
              >
                <label>
                  Contact name
                  <input
                    value={officeForm.contactName}
                    onChange={(e) =>
                      setOfficeForm({
                        ...officeForm,
                        contactName: e.target.value,
                      })
                    }
                    style={{
                      display: "block",
                      width: "100%",
                      marginTop: 4,
                      padding: 8,
                      boxSizing: "border-box",
                    }}
                  />
                </label>
                <label>
                  Contact email
                  <input
                    type="email"
                    value={officeForm.contactEmail}
                    onChange={(e) =>
                      setOfficeForm({
                        ...officeForm,
                        contactEmail: e.target.value,
                      })
                    }
                    style={{
                      display: "block",
                      width: "100%",
                      marginTop: 4,
                      padding: 8,
                      boxSizing: "border-box",
                    }}
                  />
                </label>
              </div>
              <button
                type="submit"
                disabled={officeSaving}
                style={{
                  padding: "10px 16px",
                  background: "#4a90e2",
                  color: "#fff",
                  border: 0,
                  borderRadius: 6,
                  cursor: "pointer",
                }}
              >
                {officeSaving ? "Saving…" : "Save page details"}
              </button>
            </form>
          </section>

          <section
            style={{
              border: "1px solid #e0e0e0",
              borderRadius: 8,
              padding: 16,
              background: "#fff",
            }}
          >
            <h2 style={{ marginTop: 0 }}>What we serve</h2>
            <p style={{ color: "#666", fontSize: 13, marginTop: 0 }}>
              Services listed on the public page (IT Services, LMS, Web Updates,
              etc.).
            </p>

            <form
              onSubmit={saveOffering}
              style={{
                background: "#f7f9fc",
                padding: 14,
                borderRadius: 8,
                marginBottom: 16,
              }}
            >
              <h3 style={{ marginTop: 0 }}>
                {editingOfferingId ? "Edit service" : "Add service"}
              </h3>
              <label style={{ display: "block", marginBottom: 10 }}>
                Title *
                <input
                  value={offeringForm.title}
                  onChange={(e) =>
                    setOfferingForm({ ...offeringForm, title: e.target.value })
                  }
                  style={{
                    display: "block",
                    width: "100%",
                    marginTop: 4,
                    padding: 8,
                    boxSizing: "border-box",
                  }}
                />
              </label>
              <label style={{ display: "block", marginBottom: 10 }}>
                Description
                <textarea
                  value={offeringForm.description}
                  onChange={(e) =>
                    setOfferingForm({
                      ...offeringForm,
                      description: e.target.value,
                    })
                  }
                  rows={3}
                  style={{
                    display: "block",
                    width: "100%",
                    marginTop: 4,
                    padding: 8,
                    boxSizing: "border-box",
                  }}
                />
              </label>
              <div
                style={{
                  display: "flex",
                  gap: 16,
                  alignItems: "center",
                  marginBottom: 12,
                  flexWrap: "wrap",
                }}
              >
                <label>
                  Order
                  <input
                    type="number"
                    min={0}
                    value={offeringForm.order}
                    onChange={(e) =>
                      setOfferingForm({
                        ...offeringForm,
                        order: e.target.value,
                      })
                    }
                    style={{
                      display: "block",
                      width: 100,
                      marginTop: 4,
                      padding: 8,
                    }}
                  />
                </label>
                <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 18 }}>
                  <input
                    type="checkbox"
                    checked={offeringForm.isActive}
                    onChange={(e) =>
                      setOfferingForm({
                        ...offeringForm,
                        isActive: e.target.checked,
                      })
                    }
                  />
                  Active (shown on website)
                </label>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="submit"
                  disabled={offeringSaving}
                  style={{
                    padding: "10px 16px",
                    background: "#4CAF50",
                    color: "#fff",
                    border: 0,
                    borderRadius: 6,
                    cursor: "pointer",
                  }}
                >
                  {offeringSaving
                    ? "Saving…"
                    : editingOfferingId
                      ? "Update service"
                      : "Add service"}
                </button>
                {editingOfferingId && (
                  <button
                    type="button"
                    onClick={resetOfferingForm}
                    style={{
                      padding: "10px 16px",
                      border: "1px solid #ccc",
                      borderRadius: 6,
                      background: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>

            {offeringsLoading ? (
              <CircularProgress size={28} />
            ) : offerings.length === 0 ? (
              <p style={{ color: "#666" }}>No services yet. Add one above.</p>
            ) : (
              offerings.map((item) => (
                <div
                  key={item._id}
                  style={{
                    border: "1px solid #e8e8e8",
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 10,
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    opacity: item.isActive === false ? 0.55 : 1,
                  }}
                >
                  <div>
                    <strong>
                      {item.order != null ? `${item.order}. ` : ""}
                      {item.title}
                    </strong>
                    {item.isActive === false && (
                      <span
                        style={{
                          marginLeft: 8,
                          fontSize: 12,
                          background: "#eee",
                          padding: "2px 6px",
                          borderRadius: 4,
                        }}
                      >
                        Hidden
                      </span>
                    )}
                    {item.description && (
                      <p style={{ margin: "6px 0 0", color: "#555", fontSize: 14 }}>
                        {item.description}
                      </p>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                    <button
                      type="button"
                      onClick={() => startEditOffering(item)}
                      style={{
                        padding: "6px 10px",
                        borderRadius: 4,
                        border: "1px solid #4a90e2",
                        background: "#fff",
                        color: "#4a90e2",
                        cursor: "pointer",
                      }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteOffering(item._id)}
                      style={{
                        padding: "6px 10px",
                        borderRadius: 4,
                        border: "1px solid #f44336",
                        background: "#fff",
                        color: "#f44336",
                        cursor: "pointer",
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </section>
        </>
      )}

      {tab === "requests" && (
        <>
          <div style={{ marginBottom: 16 }}>
            {filterBtn("pending", "Pending")}
            {filterBtn("responded", "Responded")}
          </div>

          {requestsLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
              <CircularProgress />
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: selected ? "1fr 1fr" : "1fr",
                gap: 20,
                alignItems: "start",
              }}
            >
              <div>
                {requests.length === 0 ? (
                  <p style={{ color: "#666" }}>No {filter} requests.</p>
                ) : (
                  requests.map((msg) => (
                    <div
                      key={msg._id}
                      onClick={() => openRequest(msg)}
                      style={{
                        border:
                          selected?._id === msg._id
                            ? "2px solid #4CAF50"
                            : "1px solid #e0e0e0",
                        borderRadius: 8,
                        padding: 16,
                        marginBottom: 12,
                        cursor: "pointer",
                        background:
                          selected?._id === msg._id ? "#f1f8f1" : "#fff",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 12,
                          marginBottom: 6,
                        }}
                      >
                        <strong>{msg.name}</strong>
                        <span style={{ fontSize: 12, color: "#888" }}>
                          {formatDate(msg.createdAt)}
                        </span>
                      </div>
                      <div style={{ fontSize: 14, color: "#555" }}>
                        {msg.email}
                        {msg.phone ? ` · ${msg.phone}` : ""}
                      </div>
                      {(msg.designation || msg.department) && (
                        <div style={{ fontSize: 13, color: "#666", marginTop: 4 }}>
                          {[msg.designation, msg.department]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                      )}
                      <div style={{ marginTop: 6, fontSize: 13, color: "#666" }}>
                        {(msg.profileText || "").slice(0, 100)}
                        {(msg.profileText || "").length > 100 ? "…" : ""}
                        {!msg.profileText &&
                          (msg.photoUrl || msg.resumeUrl) &&
                          " (attachment only)"}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {selected && (
                <div
                  style={{
                    border: "1px solid #ddd",
                    borderRadius: 8,
                    padding: 20,
                    background: "#fafafa",
                    position: "sticky",
                    top: 16,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 12,
                    }}
                  >
                    <h3 style={{ margin: 0 }}>Request details</h3>
                    <button
                      type="button"
                      onClick={closeDetail}
                      style={{
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        fontSize: 18,
                      }}
                    >
                      ×
                    </button>
                  </div>

                  <p>
                    <strong>Name:</strong> {selected.name}
                  </p>
                  <p>
                    <strong>Email:</strong>{" "}
                    <a href={`mailto:${selected.email}`}>{selected.email}</a>
                  </p>
                  {selected.phone && (
                    <p>
                      <strong>Phone:</strong>{" "}
                      <a href={`tel:${selected.phone}`}>{selected.phone}</a>
                    </p>
                  )}
                  {selected.designation && (
                    <p>
                      <strong>Designation:</strong> {selected.designation}
                    </p>
                  )}
                  {selected.department && (
                    <p>
                      <strong>Department:</strong> {selected.department}
                    </p>
                  )}
                  <p>
                    <strong>Status:</strong>{" "}
                    <span style={{ textTransform: "capitalize" }}>
                      {selected.status}
                    </span>
                  </p>
                  <p>
                    <strong>Received:</strong> {formatDate(selected.createdAt)}
                  </p>

                  {selected.profileText && (
                    <>
                      <p>
                        <strong>Profile text:</strong>
                      </p>
                      <div
                        style={{
                          whiteSpace: "pre-wrap",
                          background: "#fff",
                          border: "1px solid #e0e0e0",
                          borderRadius: 6,
                          padding: 12,
                          marginBottom: 16,
                        }}
                      >
                        {selected.profileText}
                      </div>
                    </>
                  )}

                  <div style={{ marginBottom: 16, display: "flex", gap: 12, flexWrap: "wrap" }}>
                    {selected.photoUrl && (
                      <a
                        href={selected.photoUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: "#4a90e2" }}
                      >
                        View photo
                      </a>
                    )}
                    {selected.resumeUrl && (
                      <a
                        href={selected.resumeUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: "#4a90e2" }}
                      >
                        Download resume
                      </a>
                    )}
                  </div>

                  {selected.photoUrl && (
                    <img
                      src={selected.photoUrl}
                      alt="Submitted profile"
                      style={{
                        maxWidth: 160,
                        borderRadius: 8,
                        marginBottom: 16,
                        display: "block",
                      }}
                    />
                  )}

                  <label
                    htmlFor="itAdminNote"
                    style={{ display: "block", fontWeight: 600, marginBottom: 6 }}
                  >
                    {(selected.respondedBy || userDisplayName)} note
                  </label>
                  <textarea
                    id="itAdminNote"
                    value={adminNote}
                    onChange={(e) => setAdminNote(e.target.value)}
                    rows={3}
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      padding: 10,
                      borderRadius: 6,
                      border: "1px solid #ccc",
                      marginBottom: 16,
                    }}
                    placeholder={`Add a note as ${userDisplayName}…`}
                  />

                  <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                    {selected.status !== "responded" ? (
                      <button
                        type="button"
                        disabled={actionLoading}
                        onClick={markResponded}
                        style={{
                          padding: "10px 16px",
                          backgroundColor: "#4CAF50",
                          color: "#fff",
                          border: "none",
                          borderRadius: 5,
                          cursor: "pointer",
                        }}
                      >
                        {actionLoading ? "Saving…" : "Mark as Responded"}
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={actionLoading}
                        onClick={markPending}
                        style={{
                          padding: "10px 16px",
                          backgroundColor: "#2196F3",
                          color: "#fff",
                          border: "none",
                          borderRadius: 5,
                          cursor: "pointer",
                        }}
                      >
                        {actionLoading ? "Saving…" : "Move to Pending"}
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={actionLoading}
                      onClick={() => deleteRequest(selected._id)}
                      style={{
                        padding: "10px 16px",
                        backgroundColor: "#f44336",
                        color: "#fff",
                        border: "none",
                        borderRadius: 5,
                        cursor: "pointer",
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ItServicesAdmin;
